import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MailIcon, LinkedinIcon, MapPinIcon, SendIcon } from 'lucide-react';
import { Button } from './ui/Button';
export function Contact() {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    organization: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormState(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Placeholder submission
    setSubmitted(true);
  };
  return <section id="contact" className="py-24 lg:py-32 bg-[#1C1F23]" aria-labelledby="contact-heading" data-id="element-231">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-232">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16" data-id="element-233">
          {/* Left — info */}
          <div data-id="element-234">
            <span className="inline-flex items-center gap-2 text-[#B08D57] text-xs font-body font-semibold tracking-[0.2em] uppercase mb-3" data-id="element-235">
              <span className="w-6 h-px bg-[#B08D57]" aria-hidden="true" data-id="element-236" />
              Contact
            </span>
            <h2 id="contact-heading" className="font-heading font-bold text-[#F7F7F5] text-3xl lg:text-4xl mb-6" data-id="element-237">
              Let's Build Something Significant
            </h2>
            <p className="font-body font-light text-[#ADBEC8] leading-relaxed mb-10" data-id="element-238">
              Available for senior director, VP, and executive leadership roles
              in product, experience, and brand systems. Also open to advisory
              engagements, board roles, and speaking opportunities.
            </p>

            <div className="space-y-6" data-id="element-239">
              <div className="flex items-center gap-4" data-id="element-240">
                <div className="w-10 h-10 rounded-lg bg-[#B08D57]/20 flex items-center justify-center flex-shrink-0" aria-hidden="true" data-id="element-241">
                  <MailIcon className="w-5 h-5 text-[#B08D57]" data-id="element-242" />
                </div>
                <div data-id="element-243">
                  <div className="font-body text-[#8FA4B0] text-xs uppercase tracking-widest mb-0.5" data-id="element-244">
                    Email
                  </div>
                  <a href="mailto:mkbigosjr@gmail.com" className="font-body text-[#F7F7F5] hover:text-[#B08D57] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B08D57] rounded" aria-label="Send email to Michael K. Bigos" data-id="element-245">
                    mkbigosjr@gmail.com
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4" data-id="element-246">
                <div className="w-10 h-10 rounded-lg bg-[#B08D57]/20 flex items-center justify-center flex-shrink-0" aria-hidden="true" data-id="element-247">
                  <LinkedinIcon className="w-5 h-5 text-[#B08D57]" data-id="element-248" />
                </div>
                <div data-id="element-249">
                  <div className="font-body text-[#8FA4B0] text-xs uppercase tracking-widest mb-0.5" data-id="element-250">
                    LinkedIn
                  </div>
                  <a href="https://www.linkedin.com/in/michaelkbigosjr/" target="_blank" rel="noopener noreferrer" className="font-body text-[#F7F7F5] hover:text-[#B08D57] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B08D57] rounded" aria-label="Visit Michael K. Bigos LinkedIn profile (opens in new tab)" data-id="element-251">
                    linkedin.com/in/michaelkbigosjr
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4" data-id="element-252">
                <div className="w-10 h-10 rounded-lg bg-[#B08D57]/20 flex items-center justify-center flex-shrink-0" aria-hidden="true" data-id="element-253">
                  <MapPinIcon className="w-5 h-5 text-[#B08D57]" data-id="element-254" />
                </div>
                <div data-id="element-255">
                  <div className="font-body text-[#8FA4B0] text-xs uppercase tracking-widest mb-0.5" data-id="element-256">
                    Location
                  </div>
                  <span className="font-body text-[#F7F7F5]" data-id="element-257">
                    Tampa, Florida · Open to Remote
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right — form */}
          <div data-id="element-258">
            {submitted ? <motion.div className="h-full flex flex-col items-center justify-center text-center py-16" initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.4
          }} data-id="element-259">
                <div className="w-16 h-16 rounded-full bg-[#B08D57]/20 flex items-center justify-center mb-6" aria-hidden="true" data-id="element-260">
                  <SendIcon className="w-7 h-7 text-[#B08D57]" data-id="element-261" />
                </div>
                <h3 className="font-heading font-bold text-[#F7F7F5] text-2xl mb-3" data-id="element-262">
                  Message Received
                </h3>
                <p className="font-body font-light text-[#ADBEC8] max-w-sm" data-id="element-263">
                  Thank you for reaching out. I'll respond within 48 hours.
                </p>
              </motion.div> : <form onSubmit={handleSubmit} className="space-y-5" aria-label="Contact form" noValidate data-id="element-264">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-265">
                  <div data-id="element-266">
                    <label htmlFor="name" className="block font-body text-[#C4D4DC] text-sm mb-2" data-id="element-267">
                      Full Name <span aria-label="required" data-id="element-268">*</span>
                    </label>
                    <input id="name" name="name" type="text" required value={formState.name} onChange={handleChange} className="w-full bg-[#2F3A45]/50 border border-[#F7F7F5]/10 rounded-lg px-4 py-3 font-body text-[#F7F7F5] text-sm placeholder-[#F7F7F5]/30 focus:outline-none focus:ring-2 focus:ring-[#B08D57] focus:border-transparent transition-all" placeholder="Your name" autoComplete="name" data-id="element-269" />
                  </div>
                  <div data-id="element-270">
                    <label htmlFor="email" className="block font-body text-[#C4D4DC] text-sm mb-2" data-id="element-271">
                      Email Address <span aria-label="required" data-id="element-272">*</span>
                    </label>
                    <input id="email" name="email" type="email" required value={formState.email} onChange={handleChange} className="w-full bg-[#2F3A45]/50 border border-[#F7F7F5]/10 rounded-lg px-4 py-3 font-body text-[#F7F7F5] text-sm placeholder-[#F7F7F5]/30 focus:outline-none focus:ring-2 focus:ring-[#B08D57] focus:border-transparent transition-all" placeholder="your@email.com" autoComplete="email" data-id="element-273" />
                  </div>
                </div>

                <div data-id="element-274">
                  <label htmlFor="organization" className="block font-body text-[#C4D4DC] text-sm mb-2" data-id="element-275">
                    Organization
                  </label>
                  <input id="organization" name="organization" type="text" value={formState.organization} onChange={handleChange} className="w-full bg-[#2F3A45]/50 border border-[#F7F7F5]/10 rounded-lg px-4 py-3 font-body text-[#F7F7F5] text-sm placeholder-[#F7F7F5]/30 focus:outline-none focus:ring-2 focus:ring-[#B08D57] focus:border-transparent transition-all" placeholder="Company or agency" autoComplete="organization" data-id="element-276" />
                </div>

                <div data-id="element-277">
                  <label htmlFor="message" className="block font-body text-[#C4D4DC] text-sm mb-2" data-id="element-278">
                    Message <span aria-label="required" data-id="element-279">*</span>
                  </label>
                  <textarea id="message" name="message" required rows={5} value={formState.message} onChange={handleChange} className="w-full bg-[#2F3A45]/50 border border-[#F7F7F5]/10 rounded-lg px-4 py-3 font-body text-[#F7F7F5] text-sm placeholder-[#F7F7F5]/30 focus:outline-none focus:ring-2 focus:ring-[#B08D57] focus:border-transparent transition-all resize-none" placeholder="Tell me about the opportunity or how I can help..." data-id="element-280" />
                </div>

                <Button type="submit" variant="primary" size="lg" className="w-full" rightIcon={<SendIcon className="w-4 h-4" aria-hidden="true" data-id="element-282" />} aria-label="Send contact message" data-id="element-281">
                  Send Message
                </Button>
              </form>}
          </div>
        </div>
      </div>
    </section>;
}